import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Avatar } from '@/components/common/Avatar'
import { Button, Card, Input, Select, DateInput } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import {
  useDeleteMeAvatar,
  useUploadMeAvatar,
} from '@/features/settings/hooks/useAvatarMutations'
import { useUpdateMe } from '@/features/settings/hooks/useUpdateMe'
import {
  editMeSchema,
  formValuesToUpdateMePayload,
  meToEditFormValues,
  type EditMeFormValues,
} from '@/features/settings/schemas/editMe.schema'
import { NATIONALITY_OPTIONS } from '@/lib/nationalityOptions'
import type { MeData } from '@/types/api.types'
import { MaritalStatus, UserGender } from '@/types/user.types'

const MAX_AVATAR_SIZE_BYTES = 5 * 1024 * 1024
const ACCEPTED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
const TODAY_ISO = new Date().toISOString().slice(0, 10)

const genderOptions: SelectOption[] = [
  { value: '', label: 'Not specified' },
  { value: UserGender.MALE, label: 'Male' },
  { value: UserGender.FEMALE, label: 'Female' },
  { value: UserGender.OTHER, label: 'Other' },
]

const maritalOptions: SelectOption[] = [
  { value: '', label: 'Not specified' },
  { value: MaritalStatus.SINGLE, label: 'Single' },
  { value: MaritalStatus.MARRIED, label: 'Married' },
  { value: MaritalStatus.DIVORCED, label: 'Divorced' },
  { value: MaritalStatus.WIDOWED, label: 'Widowed' },
  { value: MaritalStatus.SEPARATED, label: 'Separated' },
  { value: MaritalStatus.DOMESTIC_PARTNERSHIP, label: 'Domestic partnership' },
  { value: MaritalStatus.OTHER, label: 'Other' },
]

function formatCnic(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 13)
  if (digits.length <= 5) return digits
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`
}

function formatPhone(value: string): string {
  const trimmed = value.replace(/[^\d\s()+-]/g, '')
  const hasPlus = trimmed.startsWith('+')
  const body = trimmed.replace(/\+/g, '')
  return `${hasPlus ? '+' : ''}${body}`.slice(0, 20)
}

function SectionHeading({
  title,
  description,
}: {
  title: string
  description: string
}): React.ReactElement {
  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
    </div>
  )
}

export function EditMyProfileForm({ me }: { me: MeData }): React.ReactElement {
  const updateMe = useUpdateMe()
  const uploadAvatar = useUploadMeAvatar()
  const deleteAvatar = useDeleteMeAvatar()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<EditMeFormValues>({
    resolver: zodResolver(editMeSchema),
    defaultValues: meToEditFormValues(me),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    reset(meToEditFormValues(me))
  }, [me, reset])

  const displayName = [me.firstName, me.lastName].filter(Boolean).join(' ') || me.email
  const isAvatarBusy = uploadAvatar.isPending || deleteAvatar.isPending

  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    if (!ACCEPTED_AVATAR_TYPES.includes(file.type as (typeof ACCEPTED_AVATAR_TYPES)[number])) {
      setAvatarError('Please upload a JPG, PNG, or WebP image.')
      return
    }

    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      setAvatarError('Profile image must be 5 MB or smaller.')
      return
    }

    setAvatarError(null)
    uploadAvatar.mutate(file, {
      onError: () => {
        setAvatarError('Could not upload profile image. Please try again.')
      },
    })
  }

  const handleAvatarDelete = (): void => {
    setAvatarError(null)
    deleteAvatar.mutate()
  }

  const onSubmit = (values: EditMeFormValues): void => {
    updateMe.mutate(formValuesToUpdateMePayload(values), {
      onSuccess: (data) => {
        reset(meToEditFormValues(data))
      },
    })
  }

  return (
    <Card
      className="border-gray-200/80 shadow-sm dark:border-gray-700/80"
      header={
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Edit your profile
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Personal details you are allowed to manage yourself. Organization-controlled fields like email, role, status, employment, and compensation stay read-only.
            </p>
          </div>
          <div className="hidden gap-2 lg:flex">
            <Button type="button" variant="secondary" onClick={() => reset(meToEditFormValues(me))} disabled={!isDirty || updateMe.isPending}>
              Reset
            </Button>
            <Button type="submit" form="edit-my-profile-form" disabled={!isDirty} loading={updateMe.isPending}>
              {updateMe.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      }
    >
      <form id="edit-my-profile-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="rounded-2xl border border-primary/15 bg-primary/5 p-4 dark:border-primary/20 dark:bg-primary/10">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Update your personal profile
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Save works on only the fields below, so you do not need to scroll back to the top.
              </p>
            </div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-primary">
              Changes save to your own account
            </p>
          </div>
        </div>

        <section className="space-y-4">
          <SectionHeading
            title="Profile"
            description="Basic profile details shown around the workspace."
          />
          <div className="rounded-2xl border border-gray-200/80 bg-gray-50/70 p-4 dark:border-gray-700/80 dark:bg-gray-900/30">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <Avatar
                  name={displayName}
                  src={me.avatar || undefined}
                  size="lg"
                  className="h-16 w-16 rounded-2xl"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Profile image
                  </p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Upload a JPG, PNG, or WebP image up to 5 MB.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_AVATAR_TYPES.join(',')}
                  className="hidden"
                  onChange={handleAvatarSelect}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isAvatarBusy}
                >
                  {uploadAvatar.isPending ? 'Uploading…' : me.avatar ? 'Change image' : 'Upload image'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleAvatarDelete}
                  disabled={!me.avatar || isAvatarBusy}
                >
                  {deleteAvatar.isPending ? 'Removing…' : 'Remove image'}
                </Button>
              </div>
            </div>
            {avatarError && <p className="mt-3 text-sm text-danger">{avatarError}</p>}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="First name"
              {...register('firstName')}
              error={errors.firstName?.message}
              placeholder="Victoria"
            />
            <Input
              label="Last name"
              {...register('lastName')}
              error={errors.lastName?.message}
              placeholder="Weber"
            />
          </div>
        </section>

        <section className="space-y-4">
          <SectionHeading
            title="Personal info"
            description="Personal identifiers and demographic details you are allowed to maintain."
          />
          <div className="grid gap-4 md:grid-cols-2">
            <DateInput
              label="Date of birth"
              {...register('personalInfo.dateOfBirth')}
              error={errors.personalInfo?.dateOfBirth?.message}
              max={TODAY_ISO}
            />
            <Controller
              control={control}
              name="personalInfo.gender"
              render={({ field }) => (
                <Select
                  label="Gender"
                  options={genderOptions}
                  value={field.value ?? ''}
                  onChange={(value) =>
                    field.onChange(value as EditMeFormValues['personalInfo']['gender'])
                  }
                  error={errors.personalInfo?.gender?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="personalInfo.nationality"
              render={({ field }) => (
                <Select
                  label="Nationality"
                  options={NATIONALITY_OPTIONS}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  error={errors.personalInfo?.nationality?.message}
                  placeholder="Select nationality"
                  searchable
                  searchPlaceholder="Search countries…"
                />
              )}
            />
            <Controller
              control={control}
              name="personalInfo.maritalStatus"
              render={({ field }) => (
                <Select
                  label="Marital status"
                  options={maritalOptions}
                  value={field.value ?? ''}
                  onChange={(value) =>
                    field.onChange(value as EditMeFormValues['personalInfo']['maritalStatus'])
                  }
                  error={errors.personalInfo?.maritalStatus?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="personalInfo.cnicNumber"
              render={({ field }) => (
                <Input
                  label="CNIC number"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(formatCnic(event.target.value))}
                  error={errors.personalInfo?.cnicNumber?.message}
                  placeholder="35202-1234567-8"
                  inputMode="numeric"
                />
              )}
            />
            <Controller
              control={control}
              name="personalInfo.passportNumber"
              render={({ field }) => (
                <Input
                  label="Passport number"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(event.target.value.toUpperCase())}
                  error={errors.personalInfo?.passportNumber?.message}
                  placeholder="AB1234567"
                />
              )}
            />
          </div>
        </section>

        <section className="space-y-4">
          <SectionHeading
            title="Emergency contact"
            description="Contact details used if the organization needs to reach someone on your behalf."
          />
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Name"
              {...register('personalInfo.emergencyContact.name')}
              error={errors.personalInfo?.emergencyContact?.name?.message}
              placeholder="John Weber"
              autoCapitalize="words"
            />
            <Input
              label="Relationship"
              {...register('personalInfo.emergencyContact.relationship')}
              error={errors.personalInfo?.emergencyContact?.relationship?.message}
              placeholder="Brother"
              autoCapitalize="words"
            />
            <Controller
              control={control}
              name="personalInfo.emergencyContact.phone"
              render={({ field }) => (
                <Input
                  label="Phone"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(formatPhone(event.target.value))}
                  error={errors.personalInfo?.emergencyContact?.phone?.message}
                  placeholder="+92-300-1234567"
                  inputMode="tel"
                  autoComplete="tel"
                />
              )}
            />
            <Controller
              control={control}
              name="personalInfo.emergencyContact.email"
              render={({ field }) => (
                <Input
                  label="Email"
                  type="email"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(event.target.value.trimStart().toLowerCase())}
                  error={errors.personalInfo?.emergencyContact?.email?.message}
                  placeholder="john@example.com"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="email"
                />
              )}
            />
          </div>
        </section>

        <section className="space-y-4">
          <SectionHeading
            title="Contact info"
            description="Private contact channels and address details for your account."
          />
          <div className="grid gap-4 md:grid-cols-2">
            <Controller
              control={control}
              name="contactInfo.personalEmail"
              render={({ field }) => (
                <Input
                  label="Personal email"
                  type="email"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(event.target.value.trimStart().toLowerCase())}
                  error={errors.contactInfo?.personalEmail?.message}
                  placeholder="victoria.personal@example.com"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="email"
                />
              )}
            />
            <Controller
              control={control}
              name="contactInfo.phone"
              render={({ field }) => (
                <Input
                  label="Phone"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(formatPhone(event.target.value))}
                  error={errors.contactInfo?.phone?.message}
                  placeholder="+92-300-1111111"
                  inputMode="tel"
                  autoComplete="tel"
                />
              )}
            />
            <Controller
              control={control}
              name="contactInfo.alternatePhone"
              render={({ field }) => (
                <Input
                  label="Alternate phone"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(formatPhone(event.target.value))}
                  error={errors.contactInfo?.alternatePhone?.message}
                  placeholder="+92-300-2222222"
                  inputMode="tel"
                  autoComplete="tel"
                />
              )}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Input
                label="Street"
                {...register('contactInfo.address.street')}
                error={errors.contactInfo?.address?.street?.message}
                placeholder="123 Main Street"
                autoCapitalize="words"
              />
            </div>
            <Input
              label="City"
              {...register('contactInfo.address.city')}
              error={errors.contactInfo?.address?.city?.message}
              placeholder="Lahore"
              autoCapitalize="words"
            />
            <Input
              label="State"
              {...register('contactInfo.address.state')}
              error={errors.contactInfo?.address?.state?.message}
              placeholder="Punjab"
              autoCapitalize="words"
            />
            <Input
              label="Country"
              {...register('contactInfo.address.country')}
              error={errors.contactInfo?.address?.country?.message}
              placeholder="Pakistan"
              autoCapitalize="words"
            />
            <Input
              label="Postal code"
                {...register('contactInfo.address.postalCode')}
              error={errors.contactInfo?.address?.postalCode?.message}
              placeholder="54000"
              autoCapitalize="characters"
            />
          </div>
        </section>

        <div className="sticky bottom-4 z-10 -mx-1 rounded-2xl border border-gray-200/80 bg-white/95 p-3 shadow-lg backdrop-blur dark:border-gray-700/80 dark:bg-gray-900/95">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isDirty ? 'You have unsaved profile changes.' : 'All profile changes are saved.'}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => reset(meToEditFormValues(me))}
                disabled={!isDirty || updateMe.isPending}
              >
                Reset
              </Button>
              <Button type="submit" disabled={!isDirty} loading={updateMe.isPending}>
                {updateMe.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Card>
  )
}

EditMyProfileForm.displayName = 'EditMyProfileForm'
